import { useCallback, useState } from "react";
import useMerchantPortalAxios, {
  useMerchantPortalFormData,
} from "../../utils/axios";
import { useDispatch } from "react-redux";
import { setFlag } from "../../features/responseSlice";
import {
  setError as setAppError,
  setFeatureData,
  setTableData,
  setTableLoading,
  settingData,
} from "../store/slices/appSlice";
import { normalizeLastId } from "../utils/loadMore";

function base64ToFile(dataUrl, fileName) {
  if (!dataUrl) return null;

  const [meta, content] = String(dataUrl).split(",");
  if (!meta || !content) return null;

  const mimeType =
    meta.split(":")[1]?.split(";")[0] || "application/octet-stream";
  const byteCharacters = atob(content);
  const byteArray = new Uint8Array(byteCharacters.length);

  for (let index = 0; index < byteCharacters.length; index += 1) {
    byteArray[index] = byteCharacters.charCodeAt(index);
  }

  return new File([byteArray], fileName || "upload", {
    type: mimeType,
  });
}

function normalizeCardId(value) {
  if (value === null || value === undefined || value === "") return 0;

  const textValue = String(value).trim();
  if (!textValue) return 0;

  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 0
    ? numericValue
    : textValue;
}

function normalizeCorporateCardsPayload(response) {
  const payload = response?.data?.Payload ?? response?.Payload ?? response?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.CorporateCards)) return payload.CorporateCards;
  if (Array.isArray(payload?.Cards)) return payload.Cards;
  if (Array.isArray(payload?.Data)) return payload.Data;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
}

export default function useNewCardsAPI() {
  const client = useMerchantPortalAxios();
  const formClient = useMerchantPortalFormData();
  const dispatch = useDispatch();
  const [corporateCards, setCorporateCards] = useState([]);
  const [cardInfo, setCardInfo] = useState({});
  const [cardsLoading, setCardsLoading] = useState(true);
  const [cardTransactions, setCardTransactions] = useState([]);
  const [cardTransactionsLoading, setCardTransactionsLoading] = useState(true);
  const [totalNbOfPage, setTotalNbOfPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [cardHolderData, setCardHolderData] = useState([]);

  const GetCorporateCard = useCallback(async () => {
    setCardsLoading(true);
    dispatch(setTableLoading({ key: "corporateCards", loading: true }));
    dispatch(setAppError(null));

    try {
      const response = await client.post("/v1/Cards/GetCorporateCard", {
        SessionID: sessionStorage.getItem("SessionID"),
      });

      const cards = normalizeCorporateCardsPayload(response);
      setCorporateCards(cards);
      dispatch(
        setTableData({
          key: "corporateCards",
          data: cards,
          pagination: {
            page: 1,
            totalPages: 1,
            totalCount: cards.length,
          },
        }),
      );
      return cards;
    } catch (error) {
      dispatch(
        setTableData({
          key: "corporateCards",
          data: [],
          pagination: {
            page: 1,
            totalPages: 0,
            totalCount: 0,
          },
        }),
      );
      dispatch(
        setAppError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "Unable to load corporate cards.",
        ),
      );
      throw error;
    } finally {
      setCardsLoading(false);
      dispatch(setTableLoading({ key: "corporateCards", loading: false }));
    }
  }, [client, dispatch]);

  async function GetCardInfoByMobileNo(mobileNo, cardProductTypeId) {
    const response = await client.post("/v1/Cards/GetCardInfoByMobileNo", {
      SessionID: sessionStorage.getItem("SessionID"),
      MobileNo: mobileNo,
      CardproductTypeId: Number(cardProductTypeId),
    });

    const payload = response?.data?.Payload || {};
    setCardInfo(payload);
    dispatch(
      setFeatureData({
        section: "details",
        key: "corporateCards",
        value: {
          cardInfoByMobile: payload,
        },
      }),
    );
    return payload;
  }

  const GetCardTransactions = useCallback(
    async (
      cardId,
      cardHolderName,
      cardMSId,
      lastId,
      startDateString,
      endDateString,
      isExport = 0,
      options = {},
    ) => {
      const normalizedLastId = normalizeLastId(lastId);
      const exportRequest = Number(isExport) === 1;
      const shouldUpdateTable = !exportRequest && !options.skipTableUpdate;

      if (!options.silent) {
        setCardTransactionsLoading(true);
        dispatch(setTableLoading({ key: "cardTransactions", loading: true }));
      }
      dispatch(setAppError(null));

      try {
        const normalizedCardId = normalizeCardId(cardId);
        const response = await client.post("/v1/Cards/GetCardTransactions", {
          SessionID: sessionStorage.getItem("SessionID"),
          CardID: normalizedCardId,
          CardHolderName: cardHolderName,
          CardMSID: cardMSId,
          lastId: normalizedLastId,
          DateFrom: startDateString,
          DateTo: endDateString,
          PageSize: 10,
          isExport: exportRequest ? 1 : 0,
        });

        const transactions = response?.data?.Payload?.CardTransactions || [];

        if (shouldUpdateTable) {
          setCardTransactions(transactions);

          if (transactions.length === 0) {
            setTotalNbOfPage(0);
            setTotalCount(0);
          } else {
            setTotalNbOfPage(transactions[0]?.TotalPages || 0);
            setTotalCount(transactions[0]?.TotalCount || 0);
          }

          dispatch(
            setTableData({
              key: "cardTransactions",
              data: transactions,
              pagination: {
                lastId: normalizedLastId,
                totalPages: transactions.length
                  ? transactions[0]?.TotalPages || 0
                  : 0,
                totalCount: transactions.length
                  ? transactions[0]?.TotalCount || 0
                  : 0,
                pageSize: 10,
              },
            }),
          );
          dispatch(
            setFeatureData({
              section: "forms",
              key: "cardTransactions",
              value: {
                cardId: normalizedCardId,
                cardHolderName,
                cardMSId,
                startDateString,
                endDateString,
              },
            }),
          );
        }

        return transactions;
      } catch (error) {
        if (shouldUpdateTable) {
          dispatch(
            setTableData({
              key: "cardTransactions",
              data: [],
              pagination: {
                lastId: normalizedLastId,
                totalPages: 0,
                totalCount: 0,
                pageSize: 10,
              },
            }),
          );
        }
        dispatch(
          setAppError(
            error?.response?.data?.Message ||
              error?.response?.data?.message ||
              "Unable to load card transactions.",
          ),
        );
        throw error;
      } finally {
        if (!options.silent) {
          setCardTransactionsLoading(false);
          dispatch(setTableLoading({ key: "cardTransactions", loading: false }));
        }
      }
    },
    [client, dispatch],
  );

  async function UploadCardTransactionInvoice(payload) {
    const nextPayload = {
      ...payload,
      ReasonFile: payload?.ReasonFile
        ? base64ToFile(payload.ReasonFile, payload.ReasonFileName)
        : payload?.ReasonFile,
    };

    const response = await formClient.post(
      "/v1/Cards/UploadCardTransactionInvoice",
      {
        SessionID: sessionStorage.getItem("SessionID"),
        TransactionID: nextPayload.TransactionID,
        ReasonFile: nextPayload.ReasonFile,
      },
    );

    if (response?.data?.Payload?.Flag === 0) {
      dispatch(setFlag(0));
    }

    return response?.data?.Payload;
  }

  const GetCardHolderData = useCallback(async () => {
    const response = await client.post("/v1/Cards/GetCardHolderData", {
      SessionID: sessionStorage.getItem("SessionID"),
    });

    const payload = response?.data?.Payload || response?.data || [];
    const holders = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.CardHolders)
        ? payload.CardHolders
        : [];

    setCardHolderData(holders);
    dispatch(
      setFeatureData({
        section: "details",
        key: "corporateCards",
        value: {
          cardHolders: holders,
        },
      }),
    );
    return holders;
  }, [client, dispatch]);

  const GetCardDetailData = useCallback(
    async ({ Type, LastCall, CardId }) => {
      const response = await client.post("/v1/Cards/GetCardDetailData", {
        SessionID: sessionStorage.getItem("SessionID"),
        Type,
        LastCall,
        CardId,
      });

      return response?.data;
    },
    [client],
  );

  const normalizeApiBoolean = (value) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value.toLowerCase() === "true";
    if (typeof value === "number") return value === 1;
    return false;
  };

  const CanRequestDisposableCard = useCallback(async () => {
    try {
      dispatch(settingData({ field: "canRequestDisposableCard", value: null }));
      const res = await client.post("/v1/Cards/CanRequestDisposableCard", {
        SessionID: sessionStorage.getItem("SessionID"),
      });

      const payload = res?.data?.Payload;
      const rawValue =
        payload && typeof payload === "object"
          ? payload.CanRequestDisposableCard ?? payload.canRequestDisposableCard
          : payload ?? res?.data;
      const nextValue = normalizeApiBoolean(rawValue);

      dispatch(
        settingData({ field: "canRequestDisposableCard", value: nextValue }),
      );

      return nextValue;
    } catch (err) {
      console.error(err);
      dispatch(settingData({ field: "canRequestDisposableCard", value: false }));
      return false;
    }
  }, [client, dispatch]);

  return {
    GetCorporateCard,
    GetCardInfoByMobileNo,
    GetCardTransactions,
    UploadCardTransactionInvoice,
    GetCardHolderData,
    corporateCards,
    cardInfo,
    cardsLoading,
    cardTransactions,
    cardTransactionsLoading,
    totalNbOfPage,
    totalCount,
    cardHolderData,
    CanRequestDisposableCard,
    GetCardDetailData,
  };
}
